// src/components/Notifications.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Spin } from 'antd';
import { io, Socket } from 'socket.io-client';
import { useLazyGetMyNotificationsQuery, useMarkNotificationReadMutation } from '../../store';
import { useNotification } from '../../contexts';
import { useNavigate } from 'react-router-dom';
import config from '../../config';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Bell } from 'lucide-react';
import { useMarkNotificationAllReadMutation } from '../../store/rtkQuery/notification';

dayjs.extend(relativeTime);

// --- New CSS Animation for the Bell ---
const shakeStyles = `
  @keyframes bell-shake {
    0% { transform: rotate(0); }
    15% { transform: rotate(15deg); }
    30% { transform: rotate(-15deg); }
    45% { transform: rotate(10deg); }
    60% { transform: rotate(-10deg); }
    75% { transform: rotate(5deg); }
    85% { transform: rotate(-5deg); }
    100% { transform: rotate(0); }
  }
  .animate-bell-shake {
    animation: bell-shake 0.6s cubic-bezier(.36,.07,.19,.97) both;
  }
  .custom-scrollbar::-webkit-scrollbar {
    width: 4px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #e2e8f0;
    border-radius: 10px;
  }
`;

interface NotificationItem {
  id: number;
  titleText: string;
  bodyText?: string;
  bodyHtml?: string;
  linkUrl?: string;
  isRead: boolean;
  createdAt?: string;
  aggregateKey?: string;
}

interface NotificationsProps {
  userId?: number;
}

const pageSize = 20;

const Notifications: React.FC<NotificationsProps> = ({ userId }) => {
  const navigate = useNavigate();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const { showNotification } = useNotification();
  const [fetchNotifications] = useLazyGetMyNotificationsQuery();
  const [markNotificationRead] = useMarkNotificationReadMutation();
  const [markAllNotificationRead] = useMarkNotificationAllReadMutation();

  const [notificationItems, setNotificationItems] = useState<NotificationItem[]>([]);
  const [notificationPage, setNotificationPage] = useState(1);
  const [notificationTotal, setNotificationTotal] = useState(0);
  const [notificationLoadingMore, setNotificationLoadingMore] = useState(false);
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [liveUnreadCount, setLiveUnreadCount] = useState(0);

  // New state for trigger animation
  const [isShaking, setIsShaking] = useState(false);

  const notificationSocketRef = useRef<Socket | null>(null);
  const notificationApiBase = useMemo(() => {
    const base =
      (import.meta.env.VITE_ENV_HOST_URL as string | undefined) ||
      (config as any)?.urls?.host ||
      window.location.origin;
    return String(base).replace(/\/$/, '');
  }, []);

  const mergeNotifications = (existing: NotificationItem[], incoming: NotificationItem[]) => {
    const map = new Map<number, NotificationItem>();
    [...existing, ...incoming].forEach(item => {
      if (item?.id) map.set(item.id, item);
    });
    return Array.from(map.values()).sort(
      (a, b) => new Date(b?.createdAt || 0).getTime() - new Date(a?.createdAt || 0).getTime()
    );
  };

  const loadNotifications = async (targetPage: number, append = false) => {
    if (!userId) return;
    try {
      const response = await fetchNotifications({ page: targetPage, pageSize }).unwrap();
      const data = (response as any)?.data || {};
      const list = Array.isArray(data?.list) ? data.list : [];
      const total = Number(data?.count || 0);

      const serverUnreadCount = Number(data?.unreadCount || list.filter((n: any) => !n.isRead).length);

      setNotificationTotal(total);
      setNotificationPage(targetPage);
      setNotificationItems(prev => (append ? mergeNotifications(prev, list) : mergeNotifications([], list)));

      setLiveUnreadCount(serverUnreadCount);
    } catch (err) {
      console.error('Failed to load notifications', err);
    }
  };
  useEffect(() => {
    if (userId) {
      loadNotifications(1, false);
    }
  }, [userId]);
  const unreadCount = useMemo(
    () => Math.max(notificationItems.filter(n => !n.isRead).length, liveUnreadCount),
    [notificationItems, liveUnreadCount]
  );
  const totalCount = notificationItems.length;


  const hasMoreNotifications = notificationItems.length < notificationTotal;

  const handleNotificationClick = async (item: NotificationItem) => {
    if (!item.isRead) {
      try {
        await markNotificationRead(item.id).unwrap();
        setNotificationItems(prev =>
          prev.map(n => (n.id === item.id ? { ...n, isRead: true, readAt: new Date().toISOString() } : n))
        );
        setLiveUnreadCount(prev => Math.max(0, prev - 1));
      } catch (err) {
        showNotification('error', 'Error', 'Failed to mark notification as read');
      }
    }

    if (item.linkUrl) {
      const isExternal = /^https?:\/\//i.test(item.linkUrl);
      if (isExternal) {
        window.open(item.linkUrl, '_blank', 'noopener,noreferrer');
      } else {
        navigate(item.linkUrl);
      }
      setIsNotificationModalOpen(false);
    }
  };

  const handleLoadMoreNotifications = async () => {
    if (notificationLoadingMore || !hasMoreNotifications) return;
    setNotificationLoadingMore(true);
    try {
      await loadNotifications(notificationPage + 1, true);
    } finally {
      setNotificationLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!userId) return;
    const socket = io(notificationApiBase, { withCredentials: true, transports: ['websocket'] });
    notificationSocketRef.current = socket;

    socket.on('connect', () => {
      // console.log('SOCKET CONNECTED:', socket.id);
      socket.emit('joinNotificationRoom', { userId });
    });

    socket.on('notification.created', (notification: NotificationItem) => {
      setNotificationItems(prev => mergeNotifications([notification], prev));
      setLiveUnreadCount(prev => prev + (notification?.isRead ? 0 : 1));

      // Trigger Shake Animation
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 800);
    });

    socket.on('notification.updated', (notification: NotificationItem) => {
      setNotificationItems(prev => prev.map(item => (item.id === notification?.id ? notification : item)));
      // Trigger Shake Animation
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 800);
    });

    socket.on('notification.read', (payload: { id?: number; aggregateKey?: string }) => {
      setNotificationItems(prev =>
        prev.map(item => {
          const isTargetById = payload?.id && item.id === payload.id;
          const isTargetByAggregateKey = payload?.aggregateKey && item.aggregateKey === payload.aggregateKey;
          if (isTargetById || isTargetByAggregateKey) {
            return { ...item, isRead: true, readAt: new Date().toISOString() };
          }
          return item;
        })
      );
      setLiveUnreadCount(prev => Math.max(0, prev - 1));
    });

    return () => {
      socket.emit('leaveNotificationRoom', { userId });
      socket.disconnect();
      notificationSocketRef.current = null;
    };
  }, [userId, notificationApiBase]);


  const [activeTab, setActiveTab] = useState<'all' | 'unread'>('all');
  const filteredNotifications = useMemo(() => {
    if (activeTab === 'unread') {
      return notificationItems.filter(n => !n.isRead);
    }
    return notificationItems;
  }, [notificationItems, activeTab]);
  const handleMarkAllAsRead = async () => {
    try {

      await markAllNotificationRead().unwrap();
      setNotificationItems(prev =>
        prev.map(n => ({ ...n, isRead: true }))
      );

      setLiveUnreadCount(0);
    } catch (err) {
      showNotification('error', 'Error', 'Failed to mark all as read');
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsNotificationModalOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
      <style>{shakeStyles}</style>
      <div ref={wrapperRef} className="relative inline-block z-[50]">
        <button
          type="button"
          className="relative flex items-center justify-center h-9 w-9 rounded-xl hover:bg-slate-100/80 transition-all active:scale-95 focus:outline-none group"
          onClick={async () => {
            setIsNotificationModalOpen(!isNotificationModalOpen);
            if (!isNotificationModalOpen) {
              await loadNotifications(1, false);
            }
          }}
          aria-label="Open notifications"
        >
          <Badge
            count={unreadCount}
            size="small"
            offset={[2, 2]}
            styles={{
              indicator: {
                fontSize: '8px',
                height: '14px',
                minWidth: '14px',
                lineHeight: '14px',
                boxShadow: 'none'
              }
            }}
          >
            <Bell
              className={`w-5 h-5 text-slate-600 group-hover:text-slate-800 transition-colors ${isShaking ? 'animate-bell-shake text-blue-600' : ''
                }`}
              strokeWidth={2}
            />
          </Badge>
        </button>

        {isNotificationModalOpen && (
          <div
            className="absolute right-0 mt-3 w-80 max-h-[75vh] flex flex-col rounded-2xl bg-white backdrop-blur-md shadow-[0_20px_50px_rgba(0,0,0,0.15)] border border-slate-300 z-[999] overflow-hidden animate-in fade-in zoom-in-95 duration-200"
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-100/50">
              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-600 select-none">
                Notifications
              </span>

              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllAsRead}
                    className="text-[8px] font-bold text-blue-600 hover:text-blue-800 uppercase tracking-wide"
                  >
                    Mark all
                  </button>
                )}


              </div>
            </div>
            <div className="flex border-b border-slate-200 text-[9px] font-bold uppercase tracking-wider">

              <button
                onClick={() => setActiveTab('all')}
                className={`flex-1 py-2 flex items-center justify-center gap-1 ${activeTab === 'all'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-slate-500 hover:text-slate-700'
                  }`}
              >
                <span>All ({totalCount})</span>
              </button>

              <button
                onClick={() => setActiveTab('unread')}
                className={`flex-1 py-2 flex items-center justify-center gap-1 ${activeTab === 'unread'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-slate-500 hover:text-slate-700'
                  }`}
              >
                <span>Unread ({unreadCount})</span>
              </button>

            </div>
            <div className="overflow-y-auto custom-scrollbar">
              {/* ... Spin and Empty state logic ... */}

              {filteredNotifications?.length > 0 && (
                <div className="divide-y divide-slate-100">
                  {filteredNotifications?.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => handleNotificationClick(item)}
                      className={`w-full text-left px-4 py-3.5 hover:bg-blue-50/60 transition-all flex flex-col group relative ${item.isRead ? 'bg-white' : 'bg-blue-50/30'
                        }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          {/* Title Text: Darker for both read/unread */}
                          <div className={`text-[10px] leading-snug truncate ${item.isRead ? 'text-slate-700 font-semibold' : 'text-slate-950 font-bold'
                            }`}>
                            {item.titleText}
                          </div>

                          {/* Body Text: Changed from slate-400 to slate-600 */}
                          {item.bodyHtml ? (
                            <div
                              className="text-[9px] text-slate-600 mt-1 leading-relaxed line-clamp-2"
                              dangerouslySetInnerHTML={{ __html: item.bodyHtml }}
                            />
                          ) : (
                            <div className="text-[9px] text-slate-600 mt-1 leading-relaxed line-clamp-2">
                              {item.bodyText}
                            </div>
                          )}
                        </div>

                        {!item.isRead && (
                          <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-blue-600 shadow-[0_0_8px_rgba(37,99,235,0.6)] flex-shrink-0" />
                        )}
                      </div>

                      {/* Timestamp: Changed from slate-300 to slate-500 */}
                      <div className="text-[8px] font-bold text-slate-500 mt-2.5 flex items-center gap-1 uppercase tracking-wider">
                        <span className="w-1 h-1 bg-slate-400 rounded-full" />
                        {item.createdAt ? dayjs(item.createdAt).fromNow() : ''}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {filteredNotifications.length === 0 && (
                <div className="text-center text-[10px] text-slate-400 py-6">
                  No notifications
                </div>
              )}
            </div>

            {/* Footer Button: Darker text for better visibility */}
            {hasMoreNotifications && (
              <div className="p-2 border-t border-slate-200 bg-slate-50">
                <button
                  onClick={handleLoadMoreNotifications}
                  disabled={notificationLoadingMore}
                  className="w-full py-2 rounded-lg text-[9px] font-bold text-slate-600 hover:text-blue-700 hover:bg-white border border-transparent hover:border-slate-200 transition-all uppercase tracking-[0.2em] disabled:opacity-50"
                >
                  {notificationLoadingMore ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default Notifications;